export interface Fees {
  markingFee: number;
  governanceFee: number;
  redemptionFee: number;
}

export interface Investment {
  initialInvestment: number;
  monthlyInvestment: number;
  investmentPeriod: number;
  annualReturn: number;
}

export interface DataPoint {
  y: number;
  marker: { enabled: boolean };
}

export interface ReturnValues {
  value: number;
  cost: number;
  markingFee: number;
  governanceFee: number;
  redemptionFee: number;
  profitData: DataPoint[];
  costsData: DataPoint[];
}

const getFundValueAndCosts = (fees: Fees, investment: Investment): ReturnValues => {
  // Start values
  let value = investment.initialInvestment;
  let totalMarkingFee = 0;
  let totalGovernanceFee = 0;

  // Arrays to store monthly data points
  const profitDataTemp: DataPoint[] = [];
  const costsDataTemp: DataPoint[] = [];

  // Initial marking fee
  const startMarkingFee = value * (fees.markingFee / 100);
  value -= startMarkingFee;
  totalMarkingFee += startMarkingFee;

  // Total number of months
  const totalMonths = investment.investmentPeriod * 12;

  // Accumulation per month
  for (let month = 0; month < totalMonths; month += 1) {
    // Monthly interest
    value += value * (investment.annualReturn / 12 / 100);

    // Monthly investment and marking fee
    if (investment.monthlyInvestment > 0) {
      const monthlyMarkingFee = investment.monthlyInvestment * (fees.markingFee / 100);
      value += investment.monthlyInvestment - monthlyMarkingFee;
      totalMarkingFee += monthlyMarkingFee;
    }

    // Apply annual governance fee once a year (in the last month of each year)
    if ((month + 1) % 12 === 0) {
      const governanceFee = value * (fees.governanceFee / 100);
      value -= governanceFee;
      totalGovernanceFee += governanceFee;
    }

    // Determine if it's the first or final month
    const isFirstMonth = month === 0;
    const isFinalMonth = month === totalMonths - 1;

    // Store the monthly value with marker on the first and final month
    profitDataTemp.push({
      y: value,
      marker: { enabled: isFirstMonth || isFinalMonth },
    });

    // Store the cost data with marker on the first and final month
    costsDataTemp.push({
      y:
        totalMarkingFee +
        totalGovernanceFee +
        (month === totalMonths - 1 ? (value * fees.redemptionFee) / 100 : 0),
      marker: { enabled: isFirstMonth || isFinalMonth },
    });
  }

  // Redemption fee applied at the end
  const redemptionFee = value * (fees.redemptionFee / 100);
  value -= redemptionFee;

  return {
    value,
    cost: totalMarkingFee + totalGovernanceFee + redemptionFee,
    markingFee: totalMarkingFee,
    governanceFee: totalGovernanceFee,
    redemptionFee,
    profitData: profitDataTemp,
    costsData: costsDataTemp,
  };
};

export default getFundValueAndCosts;
